export type Camera = {
  x: number;
  y: number;
  scale: number;
};

export type Vec2 = { x: number; y: number };

export type Viewport = {
  width: number;
  height: number;
};

export type WorldSize = {
  width: number;
  height: number;
};

export type Bounds = {
  x: number;
  y: number;
  w: number;
  h: number;
};

export const MIN_SCALE = 0.26;
export const MAX_SCALE = 2.45;
/** Portrait home keeps the score tile below the HUD (stats + order control). */
export const HOME_TOP_GUTTER = 156;

export function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

export function panCamera(cam: Camera, dx: number, dy: number): Camera {
  return { x: cam.x + dx, y: cam.y + dy, scale: cam.scale };
}

/** Zoom so the world point currently under (originX, originY) stays there. */
export function zoomCamera(
  cam: Camera,
  factor: number,
  originX: number,
  originY: number,
  minScale = MIN_SCALE,
  maxScale = MAX_SCALE,
): Camera {
  const scale = clamp(cam.scale * factor, minScale, maxScale);
  if (scale === cam.scale) return cam;
  const wx = (originX - cam.x) / cam.scale;
  const wy = (originY - cam.y) / cam.scale;
  return {
    scale,
    x: originX - wx * scale,
    y: originY - wy * scale,
  };
}

export function screenToWorld(cam: Camera, sx: number, sy: number): Vec2 {
  return {
    x: (sx - cam.x) / cam.scale,
    y: (sy - cam.y) / cam.scale,
  };
}

export function worldToScreen(cam: Camera, wx: number, wy: number): Vec2 {
  return {
    x: wx * cam.scale + cam.x,
    y: wy * cam.scale + cam.y,
  };
}

export function worldView(cam: Camera, viewport: Viewport, margin = 0): Bounds {
  const x = (-cam.x - margin) / cam.scale;
  const y = (-cam.y - margin) / cam.scale;
  return {
    x,
    y,
    w: (viewport.width + margin * 2) / cam.scale,
    h: (viewport.height + margin * 2) / cam.scale,
  };
}

export function boundsOverlap(a: Bounds, b: Bounds): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

export function tileBounds(t: { x: number; y: number; w: number; h: number }): Bounds {
  return { x: t.x, y: t.y, w: t.w, h: t.h };
}

/**
 * Keep some of the world on-screen. If the scaled world is smaller than the
 * viewport, center it. `pad` is extra slack so the edges can drift slightly.
 */
export function clampCamera(
  cam: Camera,
  world: WorldSize,
  viewport: Viewport,
  pad = 96,
): Camera {
  const scaledW = world.width * cam.scale;
  const scaledH = world.height * cam.scale;
  let { x, y } = cam;

  if (scaledW + pad * 2 <= viewport.width) {
    x = (viewport.width - scaledW) / 2;
  } else {
    const minX = viewport.width - scaledW - pad;
    const maxX = pad;
    x = clamp(x, minX, maxX);
  }

  if (scaledH + pad * 2 <= viewport.height) {
    y = (viewport.height - scaledH) / 2;
  } else {
    const minY = viewport.height - scaledH - pad;
    const maxY = pad;
    y = clamp(y, minY, maxY);
  }

  return { x, y, scale: cam.scale };
}

/** Cover the top of the home collage with a slight overscan — screenshot-like crop. */
export function cameraOnHome(
  home: Bounds,
  viewport: Viewport,
  overscan = 1.1,
): Camera {
  if (home.w <= 0 || home.h <= 0 || viewport.width <= 0 || viewport.height <= 0) {
    return { x: 0, y: 0, scale: 1 };
  }
  const portrait = viewport.height / viewport.width > 1.15;
  const aspect = viewport.height / viewport.width;
  const frameW = portrait
    ? Math.min(home.w, Math.max(680, viewport.width * 2.15))
    : home.w;
  const frame: Bounds = {
    x: home.x,
    y: home.y,
    w: frameW,
    h: Math.min(home.h, frameW * aspect),
  };
  const cover = Math.max(viewport.width / frame.w, viewport.height / frame.h);
  const widthFit = viewport.width / frame.w;
  const scale = clamp(
    portrait ? widthFit * 1.08 : cover * overscan,
    MIN_SCALE,
    MAX_SCALE,
  );
  const cx = frame.x + frame.w / 2;
  if (portrait) {
    return {
      scale,
      x: 12 - frame.x * scale,
      y: HOME_TOP_GUTTER - frame.y * scale,
    };
  }
  const cy = frame.y + frame.h / 2;
  return {
    scale,
    x: viewport.width / 2 - cx * scale,
    y: viewport.height / 2 - cy * scale,
  };
}

export function stepInertia(
  velocity: Vec2,
  dtMs: number,
  dampingPerSecond = 4.6,
): Vec2 {
  const dt = Math.max(0, dtMs);
  const damp = Math.exp(-dampingPerSecond * (dt / 1000));
  const x = velocity.x * damp;
  const y = velocity.y * damp;
  if (Math.hypot(x, y) < 0.012) return { x: 0, y: 0 };
  return { x, y };
}

export function integratePan(cam: Camera, velocity: Vec2, dtMs: number): Camera {
  return panCamera(cam, velocity.x * dtMs, velocity.y * dtMs);
}

export function wheelPanDeltas(event: {
  deltaX: number;
  deltaY: number;
  deltaMode: number;
}, pageLine = 16, pageHeight = 800): Vec2 {
  const unit =
    event.deltaMode === 1 ? pageLine : event.deltaMode === 2 ? pageHeight : 1;
  return { x: event.deltaX * unit, y: event.deltaY * unit };
}
